import { useCallback, useEffect, useState } from "react";
import useTitlesService, {
  TitleImportInProgressError,
} from "../../services/TitlesService";
import { TitleAuditFindings, TitleImportRun } from "../../types/TitleImport";
import PositiveButton from "../PositiveButton";
import NegativeButton from "../NegativeButton";
import NeutralButton from "../NeutralButton";

const reasonLabels: Record<string, string> = {
  BlockedTheme: "Blocked theme",
  BlockedKeyword: "Blocked keyword",
  UnwantedPlatforms: "No wanted platform",
  MissingFromIgdb: "Gone from IGDB",
};

interface TitleAuditSectionProps {
  /** True while any import or audit holds the single run slot. */
  isRunActive: boolean;
  /** The most recent completed audit pass, used to show how far through the catalogue we are. */
  lastAuditRun?: TitleImportRun;
  /** Called after starting a run so the parent can refresh its status straight away. */
  onRunStarted: () => Promise<void> | void;
}

export default function TitleAuditSection({
  isRunActive,
  lastAuditRun,
  onRunStarted,
}: TitleAuditSectionProps) {
  const { startTitleAudit, applyTitleAudit, getTitleAuditFindings } =
    useTitlesService();

  const [findings, setFindings] = useState<TitleAuditFindings | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [confirmingApply, setConfirmingApply] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const refreshFindings = useCallback(async () => {
    setIsLoading(true);

    try {
      setFindings(await getTitleAuditFindings());
      // Dropped rather than reconciled: a finding that has been removed elsewhere would otherwise
      // stay ticked and be sent again.
      setSelectedIds(new Set());
      setConfirmingApply(false);
      setError(null);
    } catch (err) {
      console.error("Error fetching audit findings:", err);
      setError("Could not load the audit findings.");
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    refreshFindings();
  }, [refreshFindings]);

  // A finished run may well have changed the findings.
  useEffect(() => {
    if (!isRunActive) {
      refreshFindings();
    }
  }, [isRunActive, refreshFindings]);

  // A run rebuilds this list from scratch, so it is what the last audit found rather than a pile
  // gathered over many runs. The list is capped, and removing acts on exactly what is listed, so
  // nothing is deleted unreviewed.
  const outstanding = findings?.findings ?? [];
  const totalOutstanding = findings?.totalOutstanding ?? 0;
  const beyondCap = Math.max(0, totalOutstanding - outstanding.length);

  const selectedCount = selectedIds.size;
  const allSelected = outstanding.length > 0 && selectedCount === outstanding.length;

  const toggleOne = (findingId: string) => {
    setConfirmingApply(false);
    setSelectedIds((previous) => {
      const next = new Set(previous);

      if (next.has(findingId)) {
        next.delete(findingId);
      } else {
        next.add(findingId);
      }

      return next;
    });
  };

  const toggleAll = () => {
    setConfirmingApply(false);
    setSelectedIds(
      allSelected ? new Set() : new Set(outstanding.map((f) => f.id))
    );
  };

  /** Ticks every finding flagged for the given reason, for clearing a whole category at once. */
  const selectByReason = (reason: string) => {
    setConfirmingApply(false);
    setSelectedIds(
      new Set(outstanding.filter((f) => f.reason === reason).map((f) => f.id))
    );
  };

  // Every run starts at the first title, so this reports what the last one managed rather than
  // a position to carry on from. A run that stopped early leaves its position behind.
  const isComplete =
    !!lastAuditRun &&
    lastAuditRun.progressTotal > 0 &&
    lastAuditRun.progressCurrent >= lastAuditRun.progressTotal;

  const auditPosition = lastAuditRun
    ? isComplete
      ? `All ${lastAuditRun.progressTotal} titles audited by the last run.`
      : `The last run stopped at ${lastAuditRun.progressCurrent} of ${lastAuditRun.progressTotal} titles. Run it again to audit the whole catalogue.`
    : null;

  const start = async () => {
    if (isStarting || isRunActive) {
      return;
    }

    setIsStarting(true);
    setError(null);
    setMessage(null);
    setConfirmingApply(false);

    try {
      await startTitleAudit(false);

      setMessage(
        "Audit queued. It will begin within about five minutes, takes a couple of minutes to work through the catalogue, and changes nothing until you apply it."
      );

      setSelectedIds(new Set());
    } catch (err) {
      if (err instanceof TitleImportInProgressError) {
        setError("An import or audit is already running. Wait for it to finish first.");
      } else {
        setError("Could not start the audit.");
      }
    } finally {
      setIsStarting(false);
      await onRunStarted();
    }
  };

  const remove = async () => {
    if (isStarting || isRunActive || selectedCount === 0) {
      return;
    }

    setIsStarting(true);
    setError(null);
    setMessage(null);
    setConfirmingApply(false);

    try {
      await applyTitleAudit(Array.from(selectedIds));

      setMessage(
        `Removal queued for ${selectedCount} title(s). It will begin within about five minutes. Anything left unticked stays flagged.`
      );
      setSelectedIds(new Set());
    } catch (err) {
      if (err instanceof TitleImportInProgressError) {
        setError("An import or audit is already running. Wait for it to finish first.");
      } else {
        setError("Could not queue the removal.");
      }
    } finally {
      setIsStarting(false);
      await onRunStarted();
    }
  };

  return (
    <div className="flex flex-col gap-3 pt-4 border-t border-cyan-900">
      <h3 className="text-lg">Audit the catalogue</h3>

      <p className="text-sm opacity-80">
        Works through the whole catalogue in one run, checking each title against
        IGDB, filling in anything missing, setting search weights, and listing what
        should be removed. Each run rebuilds the list below from scratch, so it
        always reflects the catalogue as it stands now. Nothing is deleted until you
        tick it and confirm.
      </p>

      {auditPosition && (
        <p className="text-sm">
          {auditPosition}
        </p>
      )}

      {error && (
        <div className="p-3 rounded bg-red-100 text-red-900 text-sm">{error}</div>
      )}

      {message && (
        <div className="p-3 rounded bg-blue-100 text-blue-900 text-sm">
          {message}
        </div>
      )}

      <div className="flex flex-row gap-2 flex-wrap items-center">
        <PositiveButton
          onClick={() => start()}
          disabled={isStarting || isRunActive}
          title={
            isRunActive
              ? "An import or audit is already running."
              : "Check every title against IGDB and report what should be removed"
          }
        >
          {isStarting ? "Starting..." : "Audit the catalogue"}
        </PositiveButton>

        {outstanding.length > 0 && !confirmingApply && (
          <NegativeButton
            onClick={() => {
              setConfirmingApply(true);
            }}
            disabled={isStarting || isRunActive || selectedCount === 0}
            title={
              selectedCount === 0
                ? "Tick the titles you want removed"
                : `Remove the ${selectedCount} ticked title(s)`
            }
          >
            Remove {selectedCount} selected title
            {selectedCount === 1 ? "" : "s"}
          </NegativeButton>
        )}

        {confirmingApply && (
          <>
            <span className="text-sm text-amber-300">
              Permanently delete {selectedCount} title
              {selectedCount === 1 ? "" : "s"}? Anything referencing them
              elsewhere will be left pointing at nothing.
            </span>
            <NegativeButton onClick={remove} disabled={isStarting}>
              Yes, delete
            </NegativeButton>
            <NeutralButton onClick={() => setConfirmingApply(false)}>
              Cancel
            </NeutralButton>
          </>
        )}
      </div>

      {outstanding.length > 0 && (
        <div className="flex flex-row gap-2 flex-wrap items-center text-sm">
          <span className="opacity-80">Tick all with reason:</span>
          {Array.from(new Set(outstanding.map((f) => f.reason))).map((reason) => (
            <button
              key={reason}
              type="button"
              onClick={() => selectByReason(reason)}
              className="px-2 py-1 rounded border border-cyan-900 hover:bg-cyan-300/25"
            >
              {reasonLabels[reason] ?? reason} (
              {outstanding.filter((f) => f.reason === reason).length})
            </button>
          ))}
        </div>
      )}

      {isLoading && !findings && <p className="text-sm">Loading findings...</p>}

      {findings && outstanding.length === 0 && (
        <p className="text-sm opacity-80">
          Nothing is waiting to be removed. Findings from every pass gather here,
          so you can keep auditing and clear them whenever you like.
        </p>
      )}

      {beyondCap > 0 && (
        <p className="text-sm text-amber-300">
          Showing {outstanding.length} of {totalOutstanding} waiting. Deal with
          these and the rest will appear.
        </p>
      )}

      {findings && outstanding.length > 0 && (
        <div className="overflow-x-auto max-h-96 overflow-y-auto">
          <table className="table-auto w-full text-sm">
            <thead>
              <tr>
                <th className="font-normal text-left p-2 w-8">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    aria-label="Select every listed title"
                    title={allSelected ? "Untick everything" : "Tick everything listed"}
                  />
                </th>
                <th className="font-normal text-left p-2">Title</th>
                <th className="font-normal text-left p-2">Reason</th>
                <th className="font-normal text-left p-2">Detail</th>
                <th className="font-normal text-left p-2">Flagged by pass</th>
              </tr>
            </thead>
            <tbody>
              {outstanding.map((finding) => (
                <tr
                  key={finding.id}
                  className={`border-t border-cyan-900 cursor-pointer hover:bg-cyan-300/10 ${
                    selectedIds.has(finding.id) ? "bg-cyan-300/20" : ""
                  }`}
                  onClick={() => toggleOne(finding.id)}
                >
                  <td className="p-2">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(finding.id)}
                      onChange={() => toggleOne(finding.id)}
                      // The row handles the click, so stop it counting twice.
                      onClick={(e) => e.stopPropagation()}
                      aria-label={`Select ${finding.titleName}`}
                    />
                  </td>
                  <td className="p-2">{finding.titleName}</td>
                  <td className="p-2 whitespace-nowrap">
                    {reasonLabels[finding.reason] ?? finding.reason}
                  </td>
                  <td className="p-2 opacity-80">{finding.detail}</td>
                  <td className="p-2 whitespace-nowrap opacity-70">
                    {finding.igdbId ? `IGDB ${finding.igdbId}` : "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
